/**
 * useBranchDays — «Часы работы филиала» (05.10.2026): GET /api/branches/days за период по одному
 * филиалу и правка дня (PUT /api/branches/:id/days/:date). Расчёт — на сервере
 * (routes/branch-days.ts, @ub/shared/branchDay): часы по играм дня, ручная поправка важнее,
 * расход норм блока «Филиалы» карточки расходника. Ошибки — текстом причины (dictErr).
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import { dictErr } from "../screens/eo/dictErr";

export interface BranchDay {
  date: string;
  minutes: number;
  games: number;
  from: string | null;
  to: string | null;
  source: "games" | "manual" | "closed" | "none";
  auto: { from: string; to: string; minutes: number } | null;
  note: string;
  /** consumable_id → расход за день в единицах расходника. */
  use: Record<string, number>;
}

export interface BranchNorm {
  consumable_id: string; name: string; unit: string; cost: number;
  qty: number; per_hour: number; round_to: number;
  /** Итог за период: единицы и рубли (единицы × стоимость за единицу). */
  total: number; sum: number;
}

export interface BranchDaysWire { id: string; name: string; minutes_total: number; days: BranchDay[]; norms: BranchNorm[] }

export type DayEdit = { from: string; to: string } | { closed: true } | { clear: true };

/** Перечитать раздел: сохранили карточку расходника (нормы филиала) или потянули список вниз. */
export const BRANCH_DAYS_RELOAD = "ub:branch-days-reload";
export const reloadBranchDays = () => window.dispatchEvent(new Event(BRANCH_DAYS_RELOAD));

export function useBranchDays(branchId: string | null, from: string, to: string, enabled: boolean) {
  /* данные помечены запросом: при смене филиала/периода чужие цифры не показываются, пока грузятся свои */
  const key = `${branchId}|${from}|${to}`;
  const [got, setGot] = useState<{ key: string; v: BranchDaysWire | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled || !branchId) return;
    let dead = false;
    setLoading(true);
    setErr(null);
    api<{ branches: BranchDaysWire[] }>(`/api/branches/days?from=${from}&to=${to}&branch=${encodeURIComponent(branchId)}`)
      .then((r) => { if (!dead) setGot({ key, v: r.branches[0] ?? null }); })
      .catch((e: unknown) => { if (!dead) setErr(dictErr(e)); })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [branchId, from, to, enabled, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const on = () => setTick((n) => n + 1);
    window.addEventListener(BRANCH_DAYS_RELOAD, on);
    return () => window.removeEventListener(BRANCH_DAYS_RELOAD, on);
  }, []);

  /** null — сохранено (данные перечитываются); строка — причина отказа. */
  const setDay = useCallback(async (date: string, body: DayEdit): Promise<string | null> => {
    if (!branchId) return null;
    try {
      await api(`/api/branches/${encodeURIComponent(branchId)}/days/${date}`, { method: "PUT", body, retries: 0 });
      setTick((n) => n + 1);
      return null;
    } catch (e) {
      return dictErr(e);
    }
  }, [branchId]);

  return { data: got?.key === key ? got.v : null, loading, err, setDay, reload: () => setTick((n) => n + 1) };
}
